import React, { useState, useEffect } from "react";
import dayjs from "dayjs";
import {
  Form,
  Input,
  Select,
  Row,
  Col,
  Button,
  Space,
  Modal,
  DatePicker,
  Table,
  InputNumber,
} from "antd";
import { useSearchParams } from "react-router-dom";

import { useNavigate } from "react-router-dom";
import { PlusOutlined } from "@ant-design/icons";
import { apiRequest } from "../../../../service/apiRequest";
import { showSuccess, showError } from "../../../../components/errorHandler";
import { useAppDispatch, useAppSelector } from "../../../../redux/hooks";

const GST_SLABS = [
  { label: "0%", value: 0 },
  { label: "5%", value: 5 },
  { label: "12%", value: 12 },
  { label: "18%", value: 18 },
  { label: "28%", value: 28 },
];
interface WoodModalProps {
  visible: boolean;
  onCancel: () => void;
  onCreate: (data: any) => void;
  initialValues?: any;
  isEditMode?: boolean;
  ledgerData?: {
    ledger_id: number;
    ledger_name: string;
    company_name: string;
  }[];
}

interface GoodsRow {
  key: string;
  description: string;
  wood_type: string;
  hsn: string;
  cft: number;
  unit_id?: number;
  rate: number;
  amount: number;
  gst_rate: number; // NEW
  cgst: number; // NEW
  sgst: number;
}

interface Ledger {
  ledger_id: number;
  ledger_deals_in_company?: number;
  ledger_status?: number;
  ledger_first_name: string;
  ledger_last_name?: string;
  ledger_mobile_no?: string;
  ledger_company_name?: string;
  ledger_gstin?: string;
}
interface Products {
  product_id: number;
  product_name: string;
}
interface UnitType {
  ut_id: number;
  ut_name: string;
}
const truncateTwoDecimals = (num: number): number => {
  return Math.floor(num * 100) / 100;
};
const AddPOOther: React.FC<WoodModalProps> = ({
  visible,
  onCancel,
  onCreate,
  initialValues = null,
  isEditMode = false,
  ledgerData = [],
}) => {
  const { ledgers, unittypes, products, loaded } = useAppSelector(
    (state) => state.master,
  );
  const [searchParams] = useSearchParams();
  const categoryType = searchParams.get("type");

  const [form] = Form.useForm();
  const [goodsData, setGoodsData] = useState<GoodsRow[]>([]);
  const [isModalClose, setIsModalClose] = useState(true);

  const [dropdownOptions, setDropdownOptions] = useState<
    { label: string; value: number }[]
  >([]);
  const [piList, setPiList] = useState<any[]>([]);
  const navigate = useNavigate();

  useEffect(() => {
    if (visible) {
      const fetchPIs = async () => {
        try {
          const data = await apiRequest<any[]>({
            method: "get",
            url: "/get-performa-invoices",
          });
          setPiList(data || []);
        } catch (error) {
          console.error("Failed to fetch performa invoices:", error);
        }
      };
      fetchPIs();
    }
  }, [visible]);

  useEffect(() => {
    if (visible && initialValues) {
      form.setFieldsValue({
        ledger_id: initialValues.ledger_id || initialValues.pbill_ledger_id,
        bill_no: initialValues.bill_no || initialValues.pbill_bill_no,
        challan_no: initialValues.challan_no || initialValues.pbill_challan_no,
        pbill_po_no: initialValues.pbill_po_no || '',
        bill_date: initialValues.bill_date ? dayjs(initialValues.bill_date) : null,
        motor_vehicle_no: (initialValues.motor_vehicle_no || initialValues.pbill_motor_vehicle_no || '') ? String(initialValues.motor_vehicle_no || initialValues.pbill_motor_vehicle_no).toUpperCase() : '',
        freight_outward: initialValues.freight_outward || initialValues.pbill_fright_amount,
      });

      if (Array.isArray(initialValues.po_details)) {
        const goods = initialValues.po_details.map((item: any) => {
          const rowAmount = Number(item.pbg_amount || (item.pbg_cft || item.pbg_qty || 0) * (item.pbg_rate || 0));
          const rowCgst = Number(item.pbg_cgst || 0);
          const rowSgst = Number(item.pbg_sgst || 0);
          const totalGst = rowCgst + rowSgst;
          let gstRate = 0;
          if (rowAmount > 0) {
            gstRate = Math.round((totalGst * 100) / rowAmount);
          }
          // Match to standard GST slab option if possible
          const closestGstSlab = GST_SLABS.reduce((prev, curr) =>
            Math.abs(curr.value - gstRate) < Math.abs(prev.value - gstRate) ? curr : prev
          );

          return {
            key: item.pbg_id ? String(item.pbg_id) : Date.now().toString() + Math.random(),
            description: item.pbg_description ? Number(item.pbg_description) : undefined,
            wood_type: item.pbg_wood_type || "",
            hsn: item.pbg_hsn || "",
            cft: Number(item.pbg_cft || item.pbg_qty || 0),
            unit_id: item.pbg_unit ? Number(item.pbg_unit) : undefined,
            rate: Number(item.pbg_rate || 0),
            amount: rowAmount,
            gst_rate: closestGstSlab.value,
            cgst: rowCgst,
            sgst: rowSgst
          };
        });
        setGoodsData(goods);
      } else {
        setGoodsData([]);
      }
    } else if (visible) {
      form.resetFields();
      setGoodsData([]);
    }
  }, [visible, initialValues]);

  const addGoodsRow = () => {
    setGoodsData((prev) => [
      ...prev,
      {
        key: Date.now().toString(),
        description: "",
        wood_type: "",
        hsn: "",
        cft: 0,
        rate: 0,
        amount: 0,
        gst_rate: 0, // NEW
        cgst: 0, // NEW
        sgst: 0,
      },
    ]);
  };

  const handleGoodsChange = (
    value: any,
    key: string,
    field: keyof GoodsRow,
  ) => {
    const updated = goodsData.map((item) => {
      if (item.key === key) {
        const newItem = { ...item, [field]: value };

        // Base amount
        newItem.amount = truncateTwoDecimals(newItem.cft * newItem.rate);

        // GST calculation per product
        const gstAmount = truncateTwoDecimals(
          (newItem.amount * (newItem.gst_rate || 0)) / 100,
        );

        newItem.cgst = truncateTwoDecimals(gstAmount / 2);
        newItem.sgst = truncateTwoDecimals(gstAmount / 2);

        return newItem;
      }
      return item;
    });

    setGoodsData(updated);
  };

  const goodsColumns = [
    {
      title: "Sr",
      dataIndex: "sr",
      render: (_: any, __: any, index: number) => index + 1,
    },
    {
      title: "Product Name",
      dataIndex: "description",
      render: (_: any, record: GoodsRow) => (
        <>
          <Select
            showSearch
            placeholder="Select Product"
            value={record.description}
            onChange={(value) =>
              handleGoodsChange(value, record.key, "description")
            }
            options={products.map((w) => ({
              label: w.product_name,
              value: w.product_id, // or w.wood_id if needed
            }))}
            filterOption={(input, option) =>
              (option?.label ?? "").toLowerCase().includes(input.toLowerCase())
            }
          />
        </>
      ),
    },
    {
      title: "QTY",
      dataIndex: "cft",
      render: (_: any, record: GoodsRow) => (
        <InputNumber
          min={0}
          controls={false}
          value={record.cft}
          parser={(value = "") => Number(value.replace(/[^\d]/g, ""))}
          onChange={(value) => handleGoodsChange(value || 0, record.key, "cft")}
        />
      ),
    },
    {
      title: "Unit",
      dataIndex: "unit_id",
      render: (_: any, record: GoodsRow) => (
        <Select
          placeholder="Select Unit"
          showSearch
          value={record.unit_id}
          style={{ width: 120 }}
          onChange={(value) => handleGoodsChange(value, record.key, "unit_id")}
          options={unittypes.map((unit) => ({
            label: unit.ut_name,
            value: unit.ut_id,
          }))}
          filterOption={(input, option) =>
            (option?.label ?? "").toLowerCase().includes(input.toLowerCase())
          }
        />
      ),
    },
    {
      title: "Rate",
      dataIndex: "rate",
      render: (_: any, record: GoodsRow) => (
        <InputNumber
          min={0}
          value={record.rate}
          parser={(value = "") =>
            Number(value.replace(/[^\d.]/g, ""))
          }
          onKeyDown={(e) => {
            if (
              e.key === " " ||
              e.key === "-" ||
              e.key === "+"
            ) {
              e.preventDefault();
            }
          }}
          onChange={(value) =>
            handleGoodsChange(value || 0, record.key, "rate")
          }
        />
      ),
    },


    {
      title: "GST %",
      dataIndex: "gst_rate",
      render: (_: any, record: GoodsRow) => (
        <Select
          showSearch
          optionFilterProp="label"
          style={{ width: 90 }}
          value={record.gst_rate}
          options={GST_SLABS}
          onChange={(value) => handleGoodsChange(value, record.key, "gst_rate")}
        />
      ),
    },
    {
      title: "CGST",
      dataIndex: "cgst",
      render: (_: any, record: GoodsRow) => (
        <span>{record.cgst.toFixed(4)}</span>
      ),
    },
    {
      title: "SGST",
      dataIndex: "sgst",
      render: (_: any, record: GoodsRow) => (
        <span>{record.sgst.toFixed(4)}</span>
      ),
    },
    {
      title: "Amount",
      dataIndex: "amount",
      render: (_: any, record: GoodsRow) => (
        <span>{record.amount.toFixed(4)}</span>
      ),
    },
    {
      title: "Action",
      dataIndex: "action",
      render: (_: any, record: GoodsRow) => (
        <Button
          type="link"
          danger
          onClick={() => handleRemoveGoodsRow(record.key)}
        >
          Remove
        </Button>
      ),
    },
  ];
  const handleRemoveGoodsRow = (key: string) => {
    const updatedData = goodsData.filter((item) => item.key !== key);
    setGoodsData(updatedData);
  };

  const totalAmount = goodsData.reduce((sum, row) => sum + row.amount, 0);

  const totalCGST = goodsData.reduce((sum, row) => sum + row.cgst, 0);

  const totalSGST = goodsData.reduce((sum, row) => sum + row.sgst, 0);

  const freightOutward = form.getFieldValue("freight_outward") || 0;

  const grandTotal = truncateTwoDecimals(
    totalAmount + totalCGST + totalSGST + Number(freightOutward),
  );

  // Round off to nearest integer
  const roundedGrandTotal = Math.round(Number(grandTotal));

  // console.log(roundedGrandTotal);

  const handleSubmit = async (values: any) => {
    const payload = {
      pbill_id: isEditMode && initialValues ? initialValues.pbill_id : undefined,
      ...values,
      bill_type: categoryType,
      bill_date: values.bill_date ? dayjs(values.bill_date).format("YYYY-MM-DD") : null,
      goods: goodsData.map((row: any) => ({
        description: row.description,
        qty: row.cft,
        cft: row.cft,
        unit_id: row.unit_id,
        rate: row.rate,
        amount: row.amount,
        gst_rate: row.gst_rate,
        cgst: row.cgst,
        sgst: row.sgst,
        pbg_issued_cft: row.pbg_issued_cft || 0,
        issued_cft: row.issued_cft || row.pbg_issued_cft || 0,
      })),
      total_amount: totalAmount,
      cgst: totalCGST,
      sgst: totalSGST,
      freight_outward: freightOutward,
      grand_total: grandTotal,
    };

    try {
      const url = isEditMode ? "update-hpo" : "create-new-hpo";
      const res = await apiRequest({
        method: "post",
        url,
        data: payload,
      });

      showSuccess(
        isEditMode ? "Bill updated successfully!" : "Bill added successfully!",
      );
      form.resetFields();
      setGoodsData([]);
      onCreate(res);

      if (isModalClose || isEditMode) {
        onCancel();
      }
    } catch (err) {
      showError("Failed to submit");
    }
  };

  const formatModuleName = (str: string) =>
    str
      .split("-")
      .map((word) => word.charAt(0).toUpperCase() + word.slice(1))
      .join(" ");
  return (
    <Modal
      title={
        (isEditMode ? "Edit PO" : "Add New PO") +
        " " +
        formatModuleName(categoryType ? categoryType : "")
      }
      visible={visible}
      onCancel={onCancel}
      footer={null}
      width="95%"
      destroyOnHidden
      centered
    >
      <Form
        form={form}
        layout="vertical"
        onFinish={handleSubmit}
        autoComplete="off"
      >
        <Row gutter={16}>
          <Col span={12}>
            <Form.Item
              name="ledger_id"
              label="Company Name"
              rules={[{ required: true }]}
            >
              <Select
                options={ledgers.map((l) => ({
                  label: `${l.ledger_company_name} (${l.ledger_gstin}) `,
                  value: l.ledger_id,
                }))}
                filterOption={(input, option) =>
                  (option?.label ?? "")
                    .toLowerCase()
                    .includes(input.toLowerCase())
                }
                showSearch
                placeholder="Select Company"
              />
            </Form.Item>
          </Col>
          <Col span={6}>
            <Form.Item
              name="bill_no"
              label="Bill No"
              rules={[{ required: true }]}
            >
              <Input />
            </Form.Item>
          </Col>
          <Col span={6}>
            <Form.Item name="challan_no" label="Challan No">
              <Input />
            </Form.Item>
          </Col>
          <Col span={6}>
            <Form.Item name="bill_date" label="Bill Date">
              <DatePicker style={{ width: "100%" }} />
            </Form.Item>
          </Col>
          <Col span={6}>
            <Form.Item
              name="motor_vehicle_no"
              label="Motor Vehicle No"
              normalize={(value) => (value ? value.toUpperCase() : value)}
            >
              <Input style={{ textTransform: "uppercase" }} placeholder="Enter vehicle no" />
            </Form.Item>
          </Col>
          <Col span={6}>
            <Form.Item
              name="freight_outward"
              label="Freight Outward Amount"
            >
              <InputNumber
                min={0}
                precision={2}
                step={0.01}
                style={{ width: '100%' }}
                parser={(value): number => {
                  const parsed = value?.replace(/[^\d.]/g, '');
                  return parsed ? Number(parsed) : 0;
                }}
                onKeyDown={(e) => {
                  if (
                    e.key === ' ' ||
                    e.key === '-' ||
                    e.key === '+'
                  ) {
                    e.preventDefault();
                  }
                }}
              />
            </Form.Item>
          </Col>


          <Col span={6}>
            <Form.Item name="pbill_po_no" label="PO No">
              <Select
                showSearch
                placeholder="Select PO No"
                options={[
                  { label: "SVC 0", value: "0" },
                  ...piList.map((pi: any) => ({
                    label: pi.pi_order_no,
                    value: pi.pi_order_no,
                  })),
                ]}
                filterOption={(input, option) =>
                  String(option?.label ?? "")
                    .toLowerCase()
                    .includes(input.toLowerCase())
                }
              />
            </Form.Item>
          </Col>
        </Row>

        {/* Goods Table */}
        <Table
          dataSource={goodsData}
          columns={goodsColumns}
          pagination={false}
          footer={() => (
            <Button type="dashed" icon={<PlusOutlined />} onClick={addGoodsRow}>
              Add Row
            </Button>
          )}
        />

        {/* Totals */}
        <Row gutter={16} style={{ marginTop: 20 }}>
          <Col span={6}>
            <strong>Taxable Amount:</strong> ₹{totalAmount.toFixed(4)}
          </Col>
          <Col span={6}>
            <strong>Total CGST:</strong> ₹{totalCGST.toFixed(4)}
          </Col>
          <Col span={6}>
            <strong>Total SGST:</strong> ₹{totalSGST.toFixed(4)}
          </Col>
          <Col span={6}>
            <strong>Grand Total:</strong> ₹{grandTotal.toFixed(4)}
          </Col>
        </Row>

        {/* Buttons */}
        <Form.Item style={{ textAlign: "right", marginTop: 30 }}>
          <Space>
            <Button onClick={onCancel}>Cancel</Button>
            {!isEditMode && (
              <Button
                type="primary"
                htmlType="submit"
                onClick={() => setIsModalClose(false)}
              >
                Submit & Create New
              </Button>
            )}
            <Button
              type="primary"
              htmlType="submit"
              onClick={() => setIsModalClose(true)}
            >
              Submit
            </Button>
          </Space>
        </Form.Item>
      </Form>
    </Modal>
  );
};

export default AddPOOther;
